// @flow

import React from 'react'
import styled, { css } from 'styled-components'
import Colors from 'styles/colors'
import { mediaMax } from 'styles/media'
// import FaIcon from 'components/atoms/FaIcon'
import Icon from 'components/atoms/Icon'

const color = {
  colorType: {
    blue: {
      main: '#23729E',
      sub: '#23729E',
    },
    green: {
      main: Colors.green,
      sub: Colors.greenBdColor,
    },
    pink: {
      main: Colors.stayPink,
      sub: Colors.stayPinkBdColor,
    },
    orange: {
      main: Colors.orange,
      sub: Colors.orange,
    },
    gray: {
      main: Colors.gray9,
      sub: Colors.gray7,
    },
    grayB: {
      main: Colors.grayB,
    },
    white: {
      main: Colors.white,
      sub: Colors.white,
    },
  },
}

const defaultHeight = 30

const ButtonWrapper = styled.div`
  ${({ isRight }) =>
    isRight &&
    css`
      text-align: right;
    `}
  ${({ isLeft }) =>
    isLeft &&
    css`
      text-align: left;
    `}
  ${({ isCenter }) =>
    isCenter &&
    css`
      text-align: center;
    `}
  ${({ buttonWrapperMargin }) =>
    buttonWrapperMargin &&
    css`
      margin: ${buttonWrapperMargin};
    `}
`

const ButtonComponent = styled.div`
  display: inline-block;
  padding: 0 8px;
  height: ${defaultHeight}px;
  border-radius: 6px;
  text-align: center;
  vertical-align: middle;
  background-color: ${Colors.white};
  cursor: pointer;
  ${({ colorType }) =>
    colorType &&
    css`
    color: ${Colors.white};
    background-color: ${color.colorType[colorType].main};
    &:hover {
      background-color: ${color.colorType[colorType].sub};
    }
    ${({ isBorder }) =>
      isBorder &&
      css`
        border: 1px solid ${color.colorType[colorType].sub};
      `}
    ${({ isBottomBorder }) =>
      isBottomBorder &&
      css`
        border-bottom: 1px solid ${color.colorType[colorType].sub};
      `}
    ${({ isOnlyBorder }) =>
      isOnlyBorder &&
      css`
        color: ${color.colorType[colorType].main};
        background-color: ${Colors.white};
        border: 1px solid ${color.colorType[colorType].main};
        &:hover {
          background-color: inherit;
        }
      `}
    ${({ disabled }) =>
      disabled &&
      css`
        background-color: ${Colors.grayE};
        color: ${Colors.grayD};
        &:hover {
          background-color: ${Colors.grayE};
          cursor: not-allowed;
        }
      `}
  `}
  ${({ isOnlyText }) =>
    isOnlyText &&
    css`
      background-color: transparent;
      border: none;
      &:hover {
        background-color: inherit;
      }
      ${({ colorType }) =>
        colorType &&
        css`
          color: ${color.colorType[colorType].main};
        `};
    `}
  ${({ isCircle, buttonHeight }) =>
    isCircle &&
    css`
      border-radius: ${(buttonHeight || defaultHeight) / 2}px;
    `}
  ${({ buttonWidthPc }) =>
    buttonWidthPc &&
    css`
      width: ${buttonWidthPc}px;
    `}
  ${({ buttonWidthSp }) =>
    buttonWidthSp &&
    css`
      ${mediaMax.phone`
      width: ${buttonWidthSp}px;
    `};
    `}
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
  ${({ fullWidthSp }) =>
    fullWidthSp &&
    css`
      ${mediaMax.phone`
      width: 100%;
    `};
    `}
  ${({ buttonHeightPc }) =>
    buttonHeightPc &&
    css`
      height: ${buttonHeightPc}px;
    `}
  ${({ buttonHeightSp }) =>
    buttonHeightSp &&
    css`
      ${mediaMax.phone`
      height: ${buttonHeightSp}px;
    `};
    `}
  ${({ buttonMargin }) =>
    buttonMargin &&
    css`
      margin: ${buttonMargin};
    `}
  ${({ buttonMarginSp }) =>
    buttonMarginSp &&
    css`
      ${mediaMax.phone`
      margin: ${buttonMarginSp};
    `};
    `}
  ${({ fontSizePc }) =>
    fontSizePc &&
    css`
      font-size: ${fontSizePc}px;
    `}
  ${({ fontSizeSp }) =>
    fontSizeSp &&
    css`
      ${mediaMax.phone`
      font-size: ${fontSizeSp}px;
    `};
    `}
  ${({ fontWeight }) =>
    fontWeight &&
    css`
      font-weight: bold;
    `}
`

const ButtonInner = styled.span`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`

function getButtonInner(props) {
  return (
    <ButtonInner>
      {props.iconType && props.iconPositionType === 'left' && (
        <FaIcon
          type={props.iconType}
          name={props.iconName}
          positionType={props.iconPositionType}
        />
      )}
      {props.children
        ? props.children
        : props.iconType && (
            <FaIcon
              type={props.iconType}
              name={props.iconName}
              positionType={props.iconPositionType}
            />
          )}
      {props.iconType && props.iconPositionType === 'right' && (
        <FaIcon
          type={props.iconType}
          name={props.iconName}
          positionType={props.iconPositionType}
        />
      )}
    </ButtonInner>
  )
}

function getButton(props) {
  switch (props.type) {
    case 'anchor':
      return (
        <ButtonComponent
          as="a"
          {...props}
          onClick={props.disabled ? null : props.onClick}
        >
          {getButtonInner(props)}
        </ButtonComponent>
      )
    case 'button':
      return (
        <ButtonComponent
          as="button"
          {...props}
          onClick={props.disabled ? null : props.onClick}
        >
          {getButtonInner(props)}
        </ButtonComponent>
      )
    default:
      return (
        <ButtonComponent
          {...props}
          onClick={props.disabled ? null : props.onClick}
        >
          {getButtonInner(props)}
        </ButtonComponent>
      )
  }
}

export type PropTypes = {
  type: string,
  href?: string,
  buttonWidthSp?: string,
  buttonWidthPc?: string,
  buttonHeightPc?: string,
  buttonHeightSp?: string,
  buttonMargin?: string,
  buttonMarginSp?: ?string,
  buttonWrapperMargin?: string,
  fullWidth?: boolean,
  iconType?: string,
  iconPositionType?: string,
  colorType?: string,
  fontSizePc?: string,
  fontSizeSp?: string,
  fontWeight?: boolean,
  isBorder?: boolean,
  isBottomBorder?: boolean,
  isOnlyBorder?: boolean,
  isOnlyText?: boolean,
  isRight?: boolean,
  isLeft?: boolean,
  isCenter?: boolean,
  isCircle?: boolean,
  onClick?: Function,
  disabled?: ?boolean,
}

export default class ButtonBase extends React.Component<*> {
  props: PropTypes

  render() {
    const { isRight, isLeft, isCenter, buttonWrapperMargin } = this.props

    if (isRight || isLeft || isCenter) {
      return (
        <ButtonWrapper
          isRight={isRight}
          isLeft={isLeft}
          isCenter={isCenter}
          buttonWrapperMargin={buttonWrapperMargin}
        >
          {getButton(this.props)}
        </ButtonWrapper>
      )
    }
    return getButton(this.props)
  }
}
